import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Zap, Users, Plus, Minus, Search, Check, ClipboardList, History } from "lucide-react";
import { toast } from "sonner";
import { axiosInstance } from "../App";
import { getErrorMessage } from "../utils/errorHandler";

// Transaction action types available from the quick actions dropdown.
// Each item knows its kind ("transaction" or "bank") and the necessary type/payload.
const QUICK_ACTIONS = [
  { value: "entrada",        label: "Entrada en Efectivo",  kind: "transaction", icon: "+",  color: "text-green-600",  needsBank: false },
  { value: "salida",         label: "Salida en Efectivo",   kind: "transaction", icon: "-",  color: "text-red-600",    needsBank: false },
  { value: "premio",         label: "Premio (Salida)",      kind: "transaction", icon: "🏆", color: "text-amber-700",  needsBank: false },
  { value: "sinpe",          label: "SINPE Móvil",          kind: "transaction", icon: "📱", color: "text-blue-600",   needsBank: true },
  { value: "pago_banco",     label: "Pago por Banco/Tarjeta", kind: "transaction", icon: "💳", color: "text-purple-600", needsBank: true },
  { value: "bank_entrada",   label: "Entrada por Banco",    kind: "bank",        icon: "🏦", color: "text-emerald-600", needsBank: true },
  { value: "bank_salida",    label: "Salida por Banco",     kind: "bank",        icon: "🏦", color: "text-rose-600",   needsBank: true },
];

export default function QuickActionsCard({ cashRegister, selectedBox, onTransactionCreated }) {
  // Quick action state
  const [selectedAction, setSelectedAction] = useState("");
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionForm, setActionForm] = useState({ amount_colones: 0, amount_dollars: 0, description: "", vault_id: "" });
  const [submittingAction, setSubmittingAction] = useState(false);
  const [actionAddedCount, setActionAddedCount] = useState(0); // cuántos se registraron sin cerrar el diálogo
  const [banks, setBanks] = useState([]);

  // Cuentas por cobrar state
  const [debts, setDebts] = useState([]);
  const [credits, setCredits] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDebtId, setSelectedDebtId] = useState("");
  const [debtMode, setDebtMode] = useState(null); // "pay" | "increase" | null
  const [adjustAmount, setAdjustAmount] = useState("");
  const [submittingAdjust, setSubmittingAdjust] = useState(false);
  const [debtDialogOpen, setDebtDialogOpen] = useState(false);
  const [debtForm, setDebtForm] = useState({
    amount_colones: 0,
    amount_dollars: 0,
    transaction_type: "venta", // pay -> efectivo (venta)
    description: "",
    vault_id: "",
    new_customer_id: "",
    new_customer_name: "",
  });
  const [submittingDebt, setSubmittingDebt] = useState(false);

  // Gestión de movimientos diarios
  const [dailyOpen, setDailyOpen] = useState(false);
  const [dailyRows, setDailyRows] = useState({});   // key(lowercase name) -> row
  const [dailySearch, setDailySearch] = useState("");
  const [submittingDaily, setSubmittingDaily] = useState(false);

  // Historial de lotes de movimientos diarios
  const [batchesOpen, setBatchesOpen] = useState(false);
  const [batches, setBatches] = useState([]);
  const [batchesLoading, setBatchesLoading] = useState(false);
  const [batchDate, setBatchDate] = useState("");
  const [expandedBatch, setExpandedBatch] = useState(null);

  // Payment reference gate for SINPE / pago_banco
  const [refDialogOpen, setRefDialogOpen] = useState(false);
  const [refInput, setRefInput] = useState("");
  const [pendingPayContext, setPendingPayContext] = useState(null); // { debtId, ... }

  // Receipt after successful payment
  const [paymentReceipt, setPaymentReceipt] = useState(null);

  // Filter banks to only the one assigned to the active box. Master assigns
  // ONE bank per box from "Configuración de Caja". When a cashier registers
  // a bank/SINPE/pago-banco movement, only the assigned bank is shown.
  // If Master didn't assign a bank, we still show ALL the actions but warn
  // at submit time.
  const visibleBanks = useMemo(() => {
    if (!selectedBox) return [];
    const assignedId = selectedBox.assigned_bank_id;
    if (!assignedId) return [];
    return banks.filter(b => b.id === assignedId);
  }, [banks, selectedBox]);

  const hasBank = visibleBanks.length > 0;

  // ALL quick actions are always visible to the cashier — including SINPE,
  // bank in/out, and pago_banco. The submit handler validates whether the
  // box has a bank assigned (so user gets a clear toast instead of a silent
  // failure).
  const visibleActions = QUICK_ACTIONS;

  // If the active box doesn't use dollars, hide the Dollar input fields
  // across all dialogs and force amount_dollars=0 on submit. Default to
  // true to preserve legacy behavior on boxes without an explicit setting.
  const usesDollars = selectedBox?.uses_dollars !== false;

  const currentAction = useMemo(
    () => QUICK_ACTIONS.find(a => a.value === selectedAction),
    [selectedAction]
  );

  // ---- Fetch helpers ----
  const fetchBanks = async () => {
    try {
      const url = selectedBox ? `/banks?box_id=${selectedBox.id}` : "/banks";
      const resp = await axiosInstance.get(url);
      setBanks(resp.data || []);
    } catch (e) { /* ignore */ }
  };

  const fetchDebts = async () => {
    if (!selectedBox) return;
    try {
      const resp = await axiosInstance.get(`/debts?box_id=${selectedBox.id}&status=pendiente`);
      const all = resp.data || [];
      const onlyByCobrar = all.filter(d =>
        d.type === "por_cobrar" && ((d.amount_colones || 0) > 0 || (d.amount_dollars || 0) > 0)
      );
      setDebts(onlyByCobrar);
      // Saldos a favor (Cuentas por Pagar del cliente)
      const onlyCredits = all.filter(d =>
        d.type === "por_pagar" && ((d.amount_colones || 0) > 0 || (d.amount_dollars || 0) > 0)
      );
      setCredits(onlyCredits);
    } catch (e) { /* ignore */ }
  };

  const fetchCustomers = async () => {
    if (!selectedBox) return;
    try {
      const resp = await axiosInstance.get(`/customers?box_id=${selectedBox.id}`);
      setCustomers(resp.data || []);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    fetchBanks();
    fetchDebts();
    fetchCustomers();
    // refresh debts every 10s while card is mounted
    const i = setInterval(fetchDebts, 10000);
    return () => clearInterval(i);
  }, [selectedBox]);

  // ---- Quick action submit ----
  const openActionDialog = () => {
    if (!cashRegister) {
      toast.error("Debes abrir la caja antes de registrar movimientos");
      return;
    }
    if (!selectedAction) {
      toast.error("Selecciona una acción primero");
      return;
    }
    setActionForm({ amount_colones: 0, amount_dollars: 0, description: "", vault_id: visibleBanks[0]?.id || "" });
    setActionAddedCount(0);
    setActionDialogOpen(true);
  };

  const submitAction = async () => {
    const action = currentAction;
    if (!action) return;
    if ((parseFloat(actionForm.amount_colones) || 0) <= 0 && (parseFloat(actionForm.amount_dollars) || 0) <= 0) {
      toast.error("Ingresa un monto mayor a cero");
      return;
    }
    if (action.needsBank && !hasBank) {
      toast.error("Esta caja no tiene banco asignado. Pídele al Master que asigne uno desde Cajas → Configurar.");
      return;
    }
    if (action.needsBank && !actionForm.vault_id) {
      toast.error("Selecciona un banco/bóveda");
      return;
    }
    setSubmittingAction(true);
    try {
      if (action.kind === "bank") {
        const bank = banks.find(b => b.id === actionForm.vault_id);
        const isEntrada = action.value === "bank_entrada";
        // Bank balance check: BOTH directions consume the bank balance.
        //  - bank_entrada: dinero sale del banco hacia la caja (entra a caja, suma a esperado, resta del banco).
        //  - bank_salida: dinero sale del banco hacia afuera (NO toca caja, NO afecta esperado, resta del banco).
        const bankBalance = bank?.total_colones || 0;
        if (parseFloat(actionForm.amount_colones) > bankBalance) {
          toast.error(`Saldo insuficiente en ${bank?.name}. Disponible: ₡${bankBalance.toLocaleString('es-CR', { minimumFractionDigits: 2 })}`);
          setSubmittingAction(false);
          return;
        }
        if (isEntrada) {
          // Backend type "entrada_a_caja": resta del source_bank_id, suma a la caja
          // (registra una transacción "pago_banco" + suma a total_cash de la caja).
          await axiosInstance.post('/banks/transactions', {
            bank_id: actionForm.vault_id, // destino (no usado para entrada_a_caja, pero se requiere)
            source_bank_id: actionForm.vault_id, // banco que provee el dinero
            type: "entrada_a_caja",
            amount_colones: parseFloat(actionForm.amount_colones) || 0,
            amount_dollars: parseFloat(actionForm.amount_dollars) || 0,
            description: actionForm.description || "Entrada por banco a caja",
            cash_register_id: cashRegister.id,
          });
        } else {
          // bank_salida: solo resta del banco. Creamos también una
          // transacción "salida_banco" en la caja activa (NO afecta el
          // efectivo, solo aparece en los reportes diarios como "Salida a
          // Banco/Bóveda") usando el tipo "salida_pura".
          await axiosInstance.post('/banks/transactions', {
            bank_id: actionForm.vault_id,
            type: "salida_pura",
            amount_colones: parseFloat(actionForm.amount_colones) || 0,
            amount_dollars: parseFloat(actionForm.amount_dollars) || 0,
            description: actionForm.description || `Salida a ${bank?.name || 'banco'}`,
            box_id: cashRegister.box_id,
          });
        }
      } else {
        // Pago por Banco/Tarjeta se registra igual que SINPE (va al banco, no a caja)
        const isCard = action.value === "pago_banco";
        const payload = {
          type: isCard ? "sinpe" : action.value,
          amount_colones: parseFloat(actionForm.amount_colones) || 0,
          amount_dollars: parseFloat(actionForm.amount_dollars) || 0,
          description: actionForm.description || `${action.label} - Acción rápida`,
        };
        if (isCard) payload.channel = "tarjeta";
        if (action.needsBank && actionForm.vault_id) {
          const bank = banks.find(b => b.id === actionForm.vault_id);
          if (bank) payload.vault_name = bank.name;
        }
        await axiosInstance.post(`/transactions?box_id=${cashRegister.box_id}`, payload);
      }
      toast.success(`${action.label} registrado correctamente`);
      // Mantener el diálogo abierto para registrar varios movimientos seguidos.
      // Solo limpiamos los montos y la descripción; se cierra con el botón "Salir".
      setActionAddedCount(c => c + 1);
      setActionForm(prev => ({ ...prev, amount_colones: 0, amount_dollars: 0, description: "" }));
      if (onTransactionCreated) onTransactionCreated();
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al registrar movimiento"));
    } finally {
      setSubmittingAction(false);
    }
  };

  // ---- Debt actions ----
  // El código de cliente debe ser IDÉNTICO en todos lados. Lo resolvemos SIEMPRE desde el
  // registro del cliente (misma fuente que la página Clientes), priorizando UUID > id/cédula > nombre.
  const custMaps = useMemo(() => {
    const byId = {}, byCedula = {}, byName = {};
    customers.forEach(c => {
      if (!c) return;
      const code = c.customer_code || "";
      if (c.id) byId[c.id] = code;
      if (c.cedula) byCedula[c.cedula] = code;
      if (c.name) byName[(c.name || "").toLowerCase()] = code;
    });
    return { byId, byCedula, byName };
  }, [customers]);
  const getDebtCode = (d) => (
    custMaps.byId[d.customer_uuid] ||
    custMaps.byId[d.customer_id] ||
    custMaps.byName[(d.customer_name || "").toLowerCase()] ||
    custMaps.byCedula[d.customer_id] ||
    d.customer_code || ""
  );
  // Compara por valor numérico para tolerar diferencias de relleno de ceros (7 == 0007)
  const codeMatchesNumber = (code, term) => {
    const n = parseInt(String(code || "").replace(/\D/g, ""), 10);
    const t = parseInt(term, 10);
    return !isNaN(n) && !isNaN(t) && n === t;
  };

  const filteredDebts = useMemo(() => {
    const term = searchTerm.trim();
    if (!term) return debts;
    if (/^\d+$/.test(term)) {
      // Búsqueda por número de cliente (1 -> 0001), tolerante al relleno de ceros
      return debts.filter(d => codeMatchesNumber(getDebtCode(d), term));
    }
    const q = term.toLowerCase();
    return debts.filter(d => (d.customer_name || "").toLowerCase().includes(q));
  }, [debts, searchTerm, custMaps]);

  const filteredCustomers = useMemo(() => {
    const term = searchTerm.trim();
    if (!term) return [];
    const isNumeric = /^\d+$/.test(term);
    const q = term.toLowerCase();
    // Customers without an active debt visible (so we can create new)
    const debtCustomerNames = new Set(debts.map(d => (d.customer_name || "").toLowerCase()));
    return customers.filter(c => {
      const matches = isNumeric
        ? codeMatchesNumber(c.customer_code, term)
        : (c.name || "").toLowerCase().includes(q);
      return matches && !debtCustomerNames.has((c.name || "").toLowerCase());
    }).slice(0, 8);
  }, [customers, debts, searchTerm]);

  // ---- Saldo del cliente (deuda en rojo, a favor en verde, cero en gris) ----
  const creditByName = useMemo(() => {
    const m = {};
    credits.forEach(c => {
      const k = (c.customer_name || "").toLowerCase();
      if (!k) return;
      if (!m[k]) m[k] = { c: 0, d: 0 };
      m[k].c += c.amount_colones || 0;
      m[k].d += c.amount_dollars || 0;
    });
    return m;
  }, [credits]);
  const debtByName = useMemo(() => {
    const m = {};
    debts.forEach(d => {
      const k = (d.customer_name || "").toLowerCase();
      if (!k) return;
      if (!m[k]) m[k] = { c: 0, d: 0 };
      m[k].c += d.amount_colones || 0;
      m[k].d += d.amount_dollars || 0;
    });
    return m;
  }, [debts]);
  const balanceTargetName = useMemo(() => {
    if (selectedDebtId) {
      const d = debts.find(x => x.id === selectedDebtId);
      if (d) return d.customer_name;
    }
    const term = searchTerm.trim();
    if (term) {
      if (filteredDebts.length === 1) return filteredDebts[0].customer_name;
      if (filteredDebts.length === 0 && filteredCustomers.length === 1) return filteredCustomers[0].name;
      const t = term.toLowerCase();
      const cm = credits.find(c => (c.customer_name || "").toLowerCase().includes(t));
      if (cm) return cm.customer_name;
    }
    return null;
  }, [selectedDebtId, debts, searchTerm, filteredDebts, filteredCustomers, credits]);
  const targetBalance = useMemo(() => {
    if (!balanceTargetName) return null;
    const k = balanceTargetName.toLowerCase();
    const d = debtByName[k] || { c: 0, d: 0 };
    const cr = creditByName[k] || { c: 0, d: 0 };
    return { name: balanceTargetName, netC: d.c - cr.c, netD: d.d - cr.d };
  }, [balanceTargetName, debtByName, creditByName]);
  const fmtC = (n) => `₡${Math.abs(n).toLocaleString('es-CR', { minimumFractionDigits: 0 })}`;

  // ---- Gestión de movimientos diarios ----
  const buildDailyRows = () => {
    const groups = {};
    (debts || []).forEach(d => {
      const key = (d.customer_name || "").toLowerCase();
      if (!key) return;
      if (!groups[key]) groups[key] = { customer_name: d.customer_name, customer_id: d.customer_id, deuda: 0, cancela: "", nueva_deuda: "", a_favor: "" };
      groups[key].deuda += (d.amount_colones || 0);
    });
    (credits || []).forEach(cr => {
      const key = (cr.customer_name || "").toLowerCase();
      if (!key || groups[key]) return;
      groups[key] = { customer_name: cr.customer_name, customer_id: cr.customer_id, deuda: 0, cancela: "", nueva_deuda: "", a_favor: "" };
    });
    return groups;
  };

  const openDaily = () => {
    setDailyRows(buildDailyRows());
    setDailySearch("");
    setDailyOpen(true);
  };

  const updateDailyRow = (key, field, value) => {
    setDailyRows(prev => ({ ...prev, [key]: { ...prev[key], [field]: value } }));
  };

  const addDailyCustomer = (c) => {
    const key = (c.name || "").toLowerCase();
    if (!key) return;
    setDailyRows(prev => {
      if (prev[key]) return prev;
      return { ...prev, [key]: { customer_name: c.name, customer_id: c.id, deuda: 0, cancela: "", nueva_deuda: "", a_favor: "" } };
    });
    setDailySearch("");
  };

  const num = (v) => { const n = parseFloat(v); return isNaN(n) ? 0 : n; };
  const montoNuevo = (r) => (r.deuda || 0) - num(r.cancela) + num(r.nueva_deuda) - num(r.a_favor);
  const rowInvalid = (r) => num(r.cancela) > (r.deuda || 0) + 0.001;

  const dailyCustomerSuggestions = useMemo(() => {
    const q = dailySearch.trim().toLowerCase();
    if (!q) return [];
    return (customers || [])
      .filter(c => (c.name || "").toLowerCase().includes(q) && !dailyRows[(c.name || "").toLowerCase()])
      .slice(0, 8);
  }, [dailySearch, customers, dailyRows]);

  // Filtrado en tiempo real + orden alfabetico A-Z por nombre
  const filteredDailyRows = useMemo(() => {
    const q = dailySearch.trim().toLowerCase();
    let entries = Object.entries(dailyRows);
    if (q) entries = entries.filter(([, r]) => (r.customer_name || "").toLowerCase().includes(q));
    return entries.sort((a, b) =>
      (a[1].customer_name || "").localeCompare(b[1].customer_name || "", "es", { sensitivity: "base" })
    );
  }, [dailySearch, dailyRows]);

  // Mostrar opcion "Crear cliente nuevo" si el nombre no existe (ni en filas ni en clientes)
  const showCreateNewCustomer = useMemo(() => {
    const q = dailySearch.trim().toLowerCase();
    if (!q) return false;
    const inRows = Object.values(dailyRows).some(r => (r.customer_name || "").toLowerCase() === q);
    const inCustomers = (customers || []).some(c => (c.name || "").toLowerCase() === q);
    return !inRows && !inCustomers;
  }, [dailySearch, dailyRows, customers]);

  const createNewDailyCustomer = (rawName) => {
    const name = (rawName || "").trim();
    if (!name) return;
    const key = name.toLowerCase();
    setDailyRows(prev => prev[key] ? prev : { ...prev, [key]: { customer_name: name, customer_id: null, deuda: 0, cancela: "", nueva_deuda: "", a_favor: "" } });
    setDailySearch("");
  };

  const dailyItemsToApply = () => Object.values(dailyRows).filter(r =>
    num(r.cancela) > 0 || num(r.nueva_deuda) > 0 || num(r.a_favor) > 0
  );

  const submitDaily = async () => {
    if (!selectedBox) return;
    const rows = Object.values(dailyRows);
    if (rows.some(rowInvalid)) {
      toast.error("CANCELA no puede ser mayor que la deuda actual del cliente.");
      return;
    }
    const items = dailyItemsToApply().map(r => ({
      customer_id: r.customer_id || null,
      customer_name: r.customer_name,
      deuda_actual: r.deuda || 0,
      cancela: num(r.cancela),
      nueva_deuda: num(r.nueva_deuda),
      a_favor: num(r.a_favor),
    }));
    if (items.length === 0) {
      toast.error("No hay movimientos para registrar.");
      return;
    }
    setSubmittingDaily(true);
    try {
      const resp = await axiosInstance.post(`/debts/daily-movements`, { box_id: selectedBox.id, items });
      toast.success(`Movimientos registrados: ${resp.data?.count || items.length} cliente(s)`);
      setDailyOpen(false);
      await fetchDebts();
      if (onTransactionCreated) onTransactionCreated();
    } catch (e) {
      toast.error(getErrorMessage(e) || "No se pudieron registrar los movimientos");
    } finally {
      setSubmittingDaily(false);
    }
  };

  const fetchBatches = async (dateFilter) => {
    if (!selectedBox) return;
    setBatchesLoading(true);
    try {
      const qs = dateFilter ? `&date=${dateFilter}` : "";
      const resp = await axiosInstance.get(`/debts/daily-movements/batches?box_id=${selectedBox.id}${qs}`);
      setBatches(resp.data || []);
    } catch (e) {
      toast.error(getErrorMessage(e) || "No se pudo cargar el historial");
    } finally {
      setBatchesLoading(false);
    }
  };

  const openBatches = () => {
    setBatchDate("");
    setExpandedBatch(null);
    setBatchesOpen(true);
    fetchBatches("");
  };

  const fmtDateTime = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleString("es-CR", { timeZone: "America/Costa_Rica", dateStyle: "short", timeStyle: "short" });
    } catch (e) { return iso; }
  };

  // Cliente objetivo resuelto (para el ajuste rápido)
  const targetInfo = useMemo(() => {
    const name = balanceTargetName;
    if (!name) return null;
    const k = name.toLowerCase();
    const debt = debts.find(d => d.id === selectedDebtId) || debts.find(d => (d.customer_name || "").toLowerCase() === k) || null;
    const credit = credits.find(c => (c.customer_name || "").toLowerCase() === k) || null;
    const cust = customers.find(c => (c.name || "").toLowerCase() === k) || null;
    const src = debt || cust || credit || {};
    return {
      name,
      customer_id: src.customer_id || src.id || name,
      customer_uuid: src.customer_uuid || (cust && cust.id) || undefined,
      customer_phone: src.customer_phone || (cust && cust.phone) || undefined,
      debt, credit,
    };
  }, [balanceTargetName, debts, credits, customers, selectedDebtId]);

  const submitAdjust = async () => {
    if (!targetInfo) { toast.error("Busca o selecciona un cliente"); return; }
    const x = parseFloat(adjustAmount);
    if (!x || isNaN(x)) { toast.error("Ingresa un monto (usa negativo para pago)"); return; }
    setSubmittingAdjust(true);
    try {
      const base = {
        customer_id: targetInfo.customer_id,
        customer_uuid: targetInfo.customer_uuid,
        customer_name: targetInfo.name,
        customer_phone: targetInfo.customer_phone,
        box_id: selectedBox.id,
      };
      if (x < 0) {
        const p = Math.abs(x);
        if (targetInfo.debt) {
          await axiosInstance.patch(`/debts/${targetInfo.debt.id}/pay`, {
            transaction_type: "venta", box_id: selectedBox.id,
            amount_paid_colones: p, amount_paid_dollars: 0,
            payment_period: "today", payment_note: "Ajuste rápido (pago)",
          });
        } else {
          await axiosInstance.post(`/debts?box_id=${selectedBox.id}`, { ...base, type: "por_cobrar", amount_colones: -p, amount_dollars: 0, description: "Saldo a favor (ajuste)" });
        }
      } else {
        if (targetInfo.credit) {
          await axiosInstance.post(`/debts/reduce-credit`, { ...base, amount_colones: x, amount_dollars: 0 });
        } else {
          await axiosInstance.post(`/debts?box_id=${selectedBox.id}`, { ...base, type: "por_cobrar", amount_colones: x, amount_dollars: 0, description: "Aumento de deuda (ajuste)" });
        }
      }
      toast.success("Movimiento registrado");
      setAdjustAmount("");
      await fetchDebts();
      if (onTransactionCreated) onTransactionCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo registrar el movimiento");
    } finally {
      setSubmittingAdjust(false);
    }
  };

  const openDebtDialog = (mode) => {
    if (!cashRegister) {
      toast.error("Debes abrir la caja antes de registrar movimientos de deuda");
      return;
    }
    if (mode === "pay" && !selectedDebtId) {
      toast.error("Selecciona un cliente con deuda");
      return;
    }
    setDebtMode(mode);
    setDebtForm({
      amount_colones: 0,
      amount_dollars: 0,
      transaction_type: "venta", // efectivo
      description: "",
      vault_id: visibleBanks[0]?.id || "",
      new_customer_id: "",
      new_customer_name: "",
    });
    setDebtDialogOpen(true);
  };

  const handleSelectNewCustomer = (cust) => {
    // Switch into "increase" mode to register a brand-new debt for an existing customer
    setSelectedDebtId(""); // signal "no existing debt"
    setDebtMode("increase");
    setDebtForm({
      amount_colones: 0,
      amount_dollars: 0,
      transaction_type: "venta",
      description: "",
      vault_id: visibleBanks[0]?.id || "",
      new_customer_id: cust.id,
      new_customer_name: cust.name,
    });
    setDebtDialogOpen(true);
  };

  const submitDebtAction = async (paymentReferenceOverride) => {
    const colones = parseFloat(debtForm.amount_colones) || 0;
    const dollars = parseFloat(debtForm.amount_dollars) || 0;
    if (colones <= 0 && dollars <= 0) {
      toast.error("Ingresa un monto mayor a cero");
      return;
    }

    // SINPE / pago_banco requires a payment reference (comprobante) — gate BEFORE submitting.
    if (debtMode === "pay" && ["sinpe", "pago_banco"].includes(debtForm.transaction_type) && !paymentReferenceOverride) {
      setPendingPayContext({ colones, dollars });
      setRefInput("");
      setRefDialogOpen(true);
      return;
    }

    setSubmittingDebt(true);
    try {
      if (debtMode === "pay") {
        const debt = debts.find(d => d.id === selectedDebtId);
        if (!debt) {
          toast.error("Deuda no encontrada");
          setSubmittingDebt(false);
          return;
        }
        // Determine vault_name only when transaction_type uses banco
        const usesVault = ["sinpe", "pago_banco"].includes(debtForm.transaction_type);
        const vaultBank = banks.find(b => b.id === debtForm.vault_id);
        const noteBase = debtForm.description || "Pago rápido";
        const finalNote = paymentReferenceOverride ? `${noteBase} (Ref: ${paymentReferenceOverride})` : noteBase;
        const response = await axiosInstance.patch(`/debts/${debt.id}/pay`, {
          transaction_type: debtForm.transaction_type,
          box_id: selectedBox.id,
          amount_paid_colones: colones,
          amount_paid_dollars: dollars,
          payment_period: "today",
          payment_note: finalNote,
          payment_reference: paymentReferenceOverride || null,
          vault_name: usesVault ? (vaultBank?.name || null) : null,
        });
        toast.success(`Pago aplicado a ${debt.customer_name}`);
        if (response.data?.receipt) {
          setPaymentReceipt(response.data.receipt);
        }
      } else if (debtMode === "increase") {
        // If there's already an active debt for that customer, increase it
        if (selectedDebtId) {
          const debt = debts.find(d => d.id === selectedDebtId);
          await axiosInstance.post(`/debts/${debt.id}/increase`, {
            amount_colones: colones,
            amount_dollars: dollars,
            reason: debtForm.description || "Aumento de deuda",
          });
          toast.success(`Deuda aumentada para ${debt.customer_name}`);
        } else {
          // Create a brand-new debt for the chosen customer
          if (!debtForm.new_customer_id || !debtForm.new_customer_name) {
            toast.error("Selecciona un cliente válido");
            setSubmittingDebt(false);
            return;
          }
          await axiosInstance.post(`/debts?box_id=${selectedBox.id}`, {
            customer_id: debtForm.new_customer_id,
            customer_name: debtForm.new_customer_name,
            type: "por_cobrar",
            amount_colones: colones,
            amount_dollars: dollars,
            description: debtForm.description || "Cuenta por cobrar (acción rápida)",
            box_id: selectedBox.id,
          });
          toast.success(`Cuenta por cobrar creada para ${debtForm.new_customer_name}`);
        }
      }
      setDebtDialogOpen(false);
      setSearchTerm("");
      setSelectedDebtId("");
      fetchDebts();
      if (onTransactionCreated) onTransactionCreated();
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al registrar"));
    } finally {
      setSubmittingDebt(false);
    }
  };

  // ---- Render ----
  return (
    <>
      {/* Acciones Rápidas */}
      <Card className="bg-white/80 backdrop-blur-xl border-slate-200 shadow-lg">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Zap className="w-5 h-5 text-amber-500" />
            Acciones Rápidas
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <Select value={selectedAction} onValueChange={setSelectedAction}>
            <SelectTrigger data-testid="quick-action-select" className="w-full">
              <SelectValue placeholder="Selecciona movimiento..." />
            </SelectTrigger>
            <SelectContent>
              {visibleActions.map(a => (
                <SelectItem key={a.value} value={a.value} data-testid={`quick-action-${a.value}`}>
                  <span className="inline-flex items-center gap-2">
                    <span className={`font-bold w-5 inline-block text-center ${a.color}`}>{a.icon}</span>
                    {a.label}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            data-testid="quick-action-register-btn"
            onClick={openActionDialog}
            className="w-full bg-amber-500 hover:bg-amber-600 text-white"
            disabled={!selectedAction}
          >
            <Plus className="w-4 h-4 mr-1" />
            Registrar
          </Button>
          <p className="text-xs text-slate-500">
            Atajo para registrar entradas, salidas, SINPE, banco, premios, etc.
          </p>
        </CardContent>
      </Card>

      {/* Cuentas por Cobrar */}
      <Card className="bg-white/80 backdrop-blur-xl border-slate-200 shadow-lg">
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <Users className="w-5 h-5 text-blue-600" />
              Cuentas por Cobrar
            </CardTitle>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                data-testid="open-batch-history-btn"
                size="sm"
                variant="outline"
                onClick={openBatches}
                className="text-xs border-slate-300 text-slate-700 hover:bg-slate-50"
              >
                <History className="w-4 h-4 mr-1" />
                Historial de lotes
              </Button>
              <Button
                data-testid="open-daily-movements-btn"
                size="sm"
                variant="outline"
                onClick={openDaily}
                className="text-xs border-blue-300 text-blue-700 hover:bg-blue-50"
              >
                <ClipboardList className="w-4 h-4 mr-1" />
                Movimientos del día
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
            <Input
              data-testid="debt-search-input"
              placeholder="Buscar por nombre o número..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8"
            />
          </div>

          {targetBalance && (
            <div
              data-testid="debt-balance-display"
              className={`text-sm rounded-md px-3 py-2 font-medium ${
                targetBalance.netC > 0.5
                  ? "bg-red-50 text-red-700 border border-red-200"
                  : targetBalance.netC < -0.5
                  ? "bg-green-50 text-green-700 border border-green-200"
                  : "bg-slate-100 text-slate-600 border border-slate-200"
              }`}
            >
              <span className="text-slate-500 font-normal">Saldo de </span>
              {targetBalance.name}:{" "}
              {targetBalance.netC > 0.5 ? (
                <span data-testid="balance-debt">Debe {fmtC(targetBalance.netC)}{targetBalance.netD > 0 ? ` / $${Math.abs(targetBalance.netD).toLocaleString('es-CR',{minimumFractionDigits:2})}` : ""}</span>
              ) : targetBalance.netC < -0.5 ? (
                <span data-testid="balance-credit">Saldo a favor {fmtC(targetBalance.netC)}{targetBalance.netD < 0 ? ` / $${Math.abs(targetBalance.netD).toLocaleString('es-CR',{minimumFractionDigits:2})}` : ""}</span>
              ) : (
                <span data-testid="balance-zero">Saldo cero</span>
              )}
            </div>
          )}

          <Select value={selectedDebtId} onValueChange={setSelectedDebtId}>
            <SelectTrigger data-testid="debt-select" className="w-full">
              <SelectValue placeholder={debts.length === 0 ? "No hay deudas pendientes" : "Selecciona cliente con deuda..."} />
            </SelectTrigger>
            <SelectContent className="max-h-[260px]">
              {filteredDebts.length === 0 && (
                <div className="px-3 py-2 text-xs text-slate-500">Sin coincidencias en deudas activas</div>
              )}
              {filteredDebts.map(d => (
                <SelectItem key={d.id} value={d.id} data-testid={`debt-option-${d.id}`}>
                  <span className="flex items-center justify-between gap-3 w-full">
                    <span className="truncate font-medium">
                      {getDebtCode(d) && <span className="text-[10px] font-mono text-slate-500 mr-1">#{getDebtCode(d)}</span>}
                      {d.customer_name}
                    </span>
                    <span className="text-xs text-slate-600 whitespace-nowrap">
                      ₡{(d.amount_colones || 0).toLocaleString()}
                      {(d.amount_dollars || 0) > 0 && ` / $${(d.amount_dollars || 0).toLocaleString()}`}
                    </span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Suggestions for new customers (no active debt) */}
          {filteredCustomers.length > 0 && (
            <div className="border border-dashed border-blue-300 bg-blue-50 rounded-md p-2 space-y-1 max-h-[150px] overflow-y-auto" data-testid="debt-new-customer-suggestions">
              <p className="text-[11px] text-blue-800 font-semibold uppercase tracking-wide">Crear nueva deuda para:</p>
              {filteredCustomers.map(c => (
                <button
                  key={c.id}
                  type="button"
                  data-testid={`debt-new-customer-${c.id}`}
                  onClick={() => handleSelectNewCustomer(c)}
                  className="w-full text-left text-sm px-2 py-1 rounded hover:bg-blue-100 flex items-center gap-2"
                >
                  <Plus className="w-3.5 h-3.5 text-blue-600" />
                  <span className="truncate">
                    {c.customer_code && <span className="text-[10px] font-mono text-slate-500 mr-1">#{c.customer_code}</span>}
                    {c.name}
                  </span>
                </button>
              ))}
            </div>
          )}

          <div className="pt-1 space-y-1.5">
            <div className="flex gap-2">
              <Input
                type="number"
                step="0.01"
                value={adjustAmount}
                onChange={(e) => setAdjustAmount(e.target.value)}
                placeholder="Monto (+ aumenta / − paga)"
                data-testid="debt-adjust-amount"
                disabled={!balanceTargetName}
              />
              <Button
                data-testid="debt-adjust-submit"
                onClick={submitAdjust}
                disabled={!balanceTargetName || submittingAdjust || !adjustAmount}
                className="bg-blue-600 hover:bg-blue-700 text-white shrink-0"
              >
                {submittingAdjust ? "..." : "Registrar"}
              </Button>
            </div>
            <p className="text-[11px] text-slate-500">
              Positivo (+) aumenta la deuda o reduce el saldo a favor. Negativo (−) registra un pago; si supera la deuda, el excedente queda como saldo a favor.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* ---- Gestión de Movimientos Diarios ---- */}
      <Dialog open={dailyOpen} onOpenChange={setDailyOpen}>
        <DialogContent data-testid="daily-movements-dialog" className="max-w-5xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-blue-600" /> Movimientos del día
            </DialogTitle>
            <DialogDescription>
              CANCELA entra a caja · NUEVA DEUDA sale de caja · A FAVOR sale de caja. MONTO NUEVO = Deuda actual − Cancela + Nueva deuda − A favor.
            </DialogDescription>
          </DialogHeader>

          <div className="relative mb-2">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
            <Input
              data-testid="daily-add-customer-search"
              placeholder="Buscar cliente para filtrar o agregar (escribe un nombre)..."
              value={dailySearch}
              onChange={(e) => setDailySearch(e.target.value)}
              className="pl-8"
            />
            {(dailyCustomerSuggestions.length > 0 || showCreateNewCustomer) && (
              <div className="absolute z-20 mt-1 w-full border border-slate-200 bg-white rounded-md shadow-lg max-h-56 overflow-y-auto" data-testid="daily-customer-suggestions">
                {dailyCustomerSuggestions.map(c => (
                  <button
                    key={c.id}
                    type="button"
                    data-testid={`daily-add-customer-${c.id}`}
                    onClick={() => addDailyCustomer(c)}
                    className="w-full text-left text-sm px-3 py-2 hover:bg-blue-50 flex items-center gap-2"
                  >
                    <Plus className="w-3.5 h-3.5 text-blue-600" />
                    <span className="truncate">
                      {c.customer_code && <span className="text-[10px] font-mono text-slate-500 mr-1">#{c.customer_code}</span>}
                      {c.name}
                    </span>
                  </button>
                ))}
                {showCreateNewCustomer && (
                  <button
                    type="button"
                    data-testid="daily-create-new-customer"
                    onClick={() => createNewDailyCustomer(dailySearch)}
                    className="w-full text-left text-sm px-3 py-2 hover:bg-green-50 flex items-center gap-2 border-t border-slate-100 text-green-700 font-medium"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span className="truncate">Crear cliente nuevo: "{dailySearch.trim()}"</span>
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="max-h-[55vh] overflow-y-auto border border-slate-200 rounded-md">
            <table className="w-full text-sm" data-testid="daily-movements-table">
              <thead className="bg-slate-100 sticky top-0 z-10">
                <tr className="text-left text-xs uppercase text-slate-600">
                  <th className="p-2">Cliente</th>
                  <th className="p-2 text-right">Deuda actual</th>
                  <th className="p-2 text-right">Cancela</th>
                  <th className="p-2 text-right">Nueva deuda</th>
                  <th className="p-2 text-right">A favor</th>
                  <th className="p-2 text-right">Monto nuevo</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(dailyRows).length === 0 && (
                  <tr><td colSpan={6} className="p-6 text-center text-slate-400">No hay clientes con deuda ni saldo a favor. Usa el buscador para agregar uno.</td></tr>
                )}
                {Object.entries(dailyRows).length > 0 && filteredDailyRows.length === 0 && (
                  <tr><td colSpan={6} className="p-6 text-center text-slate-400">
                    Sin coincidencias para "{dailySearch.trim()}".{showCreateNewCustomer ? " Usa la opción de arriba para crear un cliente nuevo." : ""}
                  </td></tr>
                )}
                {filteredDailyRows.map(([key, r]) => {
                  const mn = montoNuevo(r);
                  const invalid = rowInvalid(r);
                  return (
                    <tr key={key} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`daily-row-${key}`}>
                      <td className="p-2 font-medium truncate max-w-[180px]">{r.customer_name}</td>
                      <td className="p-2 text-right text-slate-700">{fmtC(r.deuda || 0)}</td>
                      <td className="p-2">
                        <Input
                          type="number" min="0" step="0.01"
                          data-testid={`daily-cancela-${key}`}
                          value={r.cancela}
                          onChange={(e) => updateDailyRow(key, "cancela", e.target.value)}
                          className={`h-8 text-right ${invalid ? "border-red-400 focus-visible:ring-red-400" : ""}`}
                          placeholder="0"
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          type="number" min="0" step="0.01"
                          data-testid={`daily-nueva-${key}`}
                          value={r.nueva_deuda}
                          onChange={(e) => updateDailyRow(key, "nueva_deuda", e.target.value)}
                          className="h-8 text-right"
                          placeholder="0"
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          type="number" min="0" step="0.01"
                          data-testid={`daily-afavor-${key}`}
                          value={r.a_favor}
                          onChange={(e) => updateDailyRow(key, "a_favor", e.target.value)}
                          className="h-8 text-right"
                          placeholder="0"
                        />
                      </td>
                      <td className={`p-2 text-right font-semibold ${mn > 0.5 ? "text-red-600" : mn < -0.5 ? "text-green-600" : "text-slate-500"}`} data-testid={`daily-monto-nuevo-${key}`}>
                        {mn < 0 ? `-${fmtC(mn)}` : fmtC(mn)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-slate-500">
            Monto nuevo en rojo = deuda por cobrar · en verde (negativo) = cuenta por pagar al cliente (saldo a favor).
          </p>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDailyOpen(false)} data-testid="daily-cancel-btn">Cancelar</Button>
            <Button
              onClick={submitDaily}
              disabled={submittingDaily || dailyItemsToApply().length === 0}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="daily-confirm-btn"
            >
              {submittingDaily ? "Registrando..." : `Confirmar movimientos (${dailyItemsToApply().length})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---- Historial de Lotes ---- */}
      <Dialog open={batchesOpen} onOpenChange={setBatchesOpen}>
        <DialogContent data-testid="batch-history-dialog" className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-slate-600" /> Historial de lotes
            </DialogTitle>
            <DialogDescription>
              Movimientos del día registrados en {selectedBox?.name ? `la caja ${selectedBox.name}` : "esta caja"}.
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-center gap-2 mb-2">
            <Input
              type="date"
              data-testid="batch-date-filter"
              value={batchDate}
              onChange={(e) => { setBatchDate(e.target.value); fetchBatches(e.target.value); }}
              className="w-auto h-9"
            />
            {batchDate && (
              <Button size="sm" variant="ghost" onClick={() => { setBatchDate(""); fetchBatches(""); }} data-testid="batch-date-clear">
                Ver todos
              </Button>
            )}
          </div>

          <div className="max-h-[60vh] overflow-y-auto space-y-2" data-testid="batch-history-list">
            {batchesLoading && <div className="p-6 text-center text-slate-400">Cargando...</div>}
            {!batchesLoading && batches.length === 0 && (
              <div className="p-6 text-center text-slate-400">No hay lotes registrados{batchDate ? " en esta fecha" : ""}.</div>
            )}
            {!batchesLoading && batches.map(b => {
              const open = expandedBatch === b.id;
              return (
                <div key={b.id} className="border border-slate-200 rounded-md" data-testid={`batch-${b.id}`}>
                  <button
                    type="button"
                    onClick={() => setExpandedBatch(open ? null : b.id)}
                    data-testid={`batch-toggle-${b.id}`}
                    className="w-full flex items-center justify-between gap-3 px-3 py-2 hover:bg-slate-50 text-left"
                  >
                    <div className="min-w-0">
                      <div className="font-medium text-sm">{fmtDateTime(b.created_at)}</div>
                      <div className="text-xs text-slate-500 truncate">
                        Por {b.created_by} · {b.items?.length || 0} cliente(s)
                      </div>
                    </div>
                    <div className="flex items-center gap-3 text-xs whitespace-nowrap">
                      <span className="text-green-600">Cancela {fmtC(b.total_cancela || 0)}</span>
                      <span className="text-red-600">Nueva {fmtC(b.total_nueva_deuda || 0)}</span>
                      <span className="text-amber-600">A favor {fmtC(b.total_a_favor || 0)}</span>
                      <Plus className={`w-4 h-4 transition-transform ${open ? "rotate-45" : ""} text-slate-400`} />
                    </div>
                  </button>
                  {open && (
                    <div className="border-t border-slate-100 overflow-x-auto">
                      <table className="w-full text-xs" data-testid={`batch-detail-${b.id}`}>
                        <thead className="bg-slate-50 text-slate-600 uppercase">
                          <tr className="text-left">
                            <th className="p-2">Cliente</th>
                            <th className="p-2 text-right">Deuda actual</th>
                            <th className="p-2 text-right">Cancela</th>
                            <th className="p-2 text-right">Nueva deuda</th>
                            <th className="p-2 text-right">A favor</th>
                            <th className="p-2 text-right">Monto nuevo</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(b.items || []).map((it, idx) => (
                            <tr key={idx} className="border-t border-slate-100">
                              <td className="p-2 font-medium truncate max-w-[160px]">{it.customer_name}</td>
                              <td className="p-2 text-right">{fmtC(it.deuda_actual || 0)}</td>
                              <td className="p-2 text-right text-green-700">{it.cancela ? fmtC(it.cancela) : "—"}</td>
                              <td className="p-2 text-right text-red-700">{it.nueva_deuda ? fmtC(it.nueva_deuda) : "—"}</td>
                              <td className="p-2 text-right text-amber-700">{it.a_favor ? fmtC(it.a_favor) : "—"}</td>
                              <td className={`p-2 text-right font-semibold ${(it.monto_nuevo || 0) > 0.5 ? "text-red-600" : (it.monto_nuevo || 0) < -0.5 ? "text-green-600" : "text-slate-500"}`}>
                                {(it.monto_nuevo || 0) < 0 ? `-${fmtC(it.monto_nuevo)}` : fmtC(it.monto_nuevo || 0)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setBatchesOpen(false)} data-testid="batch-history-close">Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---- Quick Action Dialog ---- */}
      <Dialog open={actionDialogOpen} onOpenChange={setActionDialogOpen}>
        <DialogContent data-testid="quick-action-dialog">
          <DialogHeader>
            <DialogTitle>{currentAction?.label || "Acción"}</DialogTitle>
            <DialogDescription>
              Registra el movimiento a mano. Se aplicará a la caja activa.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className={usesDollars ? "grid grid-cols-2 gap-3" : ""}>
              <div>
                <Label>Monto Colones (₡)</Label>
                <Input
                  data-testid="action-amount-colones"
                  type="number"
                  step="0.01"
                  min="0"
                  value={actionForm.amount_colones}
                  onChange={(e) => setActionForm({ ...actionForm, amount_colones: e.target.value })}
                />
              </div>
              {usesDollars && (
                <div>
                  <Label>Monto Dólares ($)</Label>
                  <Input
                    data-testid="action-amount-dollars"
                    type="number"
                    step="0.01"
                    min="0"
                    value={actionForm.amount_dollars}
                    onChange={(e) => setActionForm({ ...actionForm, amount_dollars: e.target.value })}
                  />
                </div>
              )}
            </div>

            {currentAction?.needsBank && (
              <div>
                <Label>Banco / Bóveda</Label>
                <Select
                  value={actionForm.vault_id}
                  onValueChange={(v) => setActionForm({ ...actionForm, vault_id: v })}
                >
                  <SelectTrigger data-testid="action-bank-select">
                    <SelectValue placeholder="Selecciona banco..." />
                  </SelectTrigger>
                  <SelectContent>
                    {visibleBanks.map(b => (
                      <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label>Descripción (opcional)</Label>
              <Input
                data-testid="action-description"
                value={actionForm.description}
                onChange={(e) => setActionForm({ ...actionForm, description: e.target.value })}
                placeholder="Detalle del movimiento"
              />
            </div>
          </div>

          <DialogFooter className="flex items-center justify-between sm:justify-between gap-2">
            {actionAddedCount > 0 && (
              <span className="text-xs text-green-700 font-medium mr-auto" data-testid="action-added-count">
                {actionAddedCount} registrado{actionAddedCount === 1 ? "" : "s"} en esta ventana
              </span>
            )}
            <Button
              variant="outline"
              data-testid="action-exit"
              onClick={() => { setActionDialogOpen(false); setSelectedAction(""); setActionAddedCount(0); }}
            >
              Salir
            </Button>
            <Button
              data-testid="action-submit"
              onClick={submitAction}
              disabled={submittingAction}
              className="bg-amber-500 hover:bg-amber-600 text-white"
            >
              {submittingAction ? "Registrando..." : "Registrar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---- Debt action dialog ---- */}
      <Dialog open={debtDialogOpen} onOpenChange={setDebtDialogOpen}>
        <DialogContent data-testid="debt-dialog">
          <DialogHeader>
            <DialogTitle>
              {debtMode === "pay" && "Pagar deuda"}
              {debtMode === "increase" && (selectedDebtId ? "Aumentar deuda" : "Nueva cuenta por cobrar")}
            </DialogTitle>
            <DialogDescription>
              {debtMode === "pay" && (() => {
                const d = debts.find(x => x.id === selectedDebtId);
                if (!d) return "Selecciona una deuda";
                return `${d.customer_name} — Saldo actual: ₡${(d.amount_colones || 0).toLocaleString()}${(d.amount_dollars || 0) > 0 ? ` / $${(d.amount_dollars || 0).toLocaleString()}` : ''}`;
              })()}
              {debtMode === "increase" && selectedDebtId && (() => {
                const d = debts.find(x => x.id === selectedDebtId);
                return d ? `${d.customer_name} — Saldo actual: ₡${(d.amount_colones || 0).toLocaleString()}` : "";
              })()}
              {debtMode === "increase" && !selectedDebtId && (debtForm.new_customer_name || "Nuevo cliente")}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className={usesDollars ? "grid grid-cols-2 gap-3" : ""}>
              <div>
                <Label>Monto Colones (₡)</Label>
                <Input
                  data-testid="debt-amount-colones"
                  type="number"
                  step="0.01"
                  min="0"
                  value={debtForm.amount_colones}
                  onChange={(e) => setDebtForm({ ...debtForm, amount_colones: e.target.value })}
                />
              </div>
              {usesDollars && (
                <div>
                  <Label>Monto Dólares ($)</Label>
                  <Input
                    data-testid="debt-amount-dollars"
                    type="number"
                    step="0.01"
                    min="0"
                    value={debtForm.amount_dollars}
                    onChange={(e) => setDebtForm({ ...debtForm, amount_dollars: e.target.value })}
                  />
                </div>
              )}
            </div>

            {debtMode === "pay" && (
              <>
                <div>
                  <Label>Método de pago</Label>
                  <Select
                    value={debtForm.transaction_type}
                    onValueChange={(v) => setDebtForm({ ...debtForm, transaction_type: v })}
                  >
                    <SelectTrigger data-testid="debt-payment-method">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="venta">Efectivo</SelectItem>
                      {hasBank && <SelectItem value="sinpe">SINPE Móvil</SelectItem>}
                      {hasBank && <SelectItem value="pago_banco">Pago por Banco/Tarjeta</SelectItem>}
                    </SelectContent>
                  </Select>
                </div>
                {["sinpe", "pago_banco"].includes(debtForm.transaction_type) && (
                  <div>
                    <Label>Banco / Bóveda</Label>
                    <Select
                      value={debtForm.vault_id}
                      onValueChange={(v) => setDebtForm({ ...debtForm, vault_id: v })}
                    >
                      <SelectTrigger data-testid="debt-bank-select">
                        <SelectValue placeholder="Selecciona banco..." />
                      </SelectTrigger>
                      <SelectContent>
                        {visibleBanks.map(b => (
                          <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </>
            )}

            <div>
              <Label>{debtMode === "pay" ? "Nota del pago" : "Razón / Descripción"}</Label>
              <Input
                data-testid="debt-description"
                value={debtForm.description}
                onChange={(e) => setDebtForm({ ...debtForm, description: e.target.value })}
                placeholder={debtMode === "pay" ? "Detalle (opcional)" : "Razón del aumento"}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDebtDialogOpen(false)}>Cancelar</Button>
            <Button
              data-testid="debt-submit"
              onClick={() => submitDebtAction()}
              disabled={submittingDebt}
              className={debtMode === "pay" ? "bg-green-600 hover:bg-green-700 text-white" : "bg-orange-500 hover:bg-orange-600 text-white"}
            >
              {submittingDebt ? "Procesando..." : (debtMode === "pay" ? "Confirmar pago" : (selectedDebtId ? "Aumentar deuda" : "Crear cuenta"))}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment reference dialog (SINPE / Banco) */}
      <Dialog open={refDialogOpen} onOpenChange={setRefDialogOpen}>
        <DialogContent className="max-w-sm" data-testid="quick-payment-ref-dialog">
          <DialogHeader>
            <DialogTitle>Número de comprobante</DialogTitle>
            <DialogDescription>
              El pago por {debtForm.transaction_type === "sinpe" ? "SINPE Móvil" : "Banco/Tarjeta"} requiere el número de comprobante (referencia bancaria).
            </DialogDescription>
          </DialogHeader>
          <Input
            autoFocus
            placeholder="Ej: 123456789 o últimos 6 dígitos"
            value={refInput}
            onChange={(e) => setRefInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && refInput.trim()) {
                setRefDialogOpen(false);
                submitDebtAction(refInput.trim());
              }
            }}
            data-testid="quick-payment-ref-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRefDialogOpen(false)}>Cancelar</Button>
            <Button
              disabled={!refInput.trim()}
              onClick={() => { setRefDialogOpen(false); submitDebtAction(refInput.trim()); }}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="quick-payment-ref-confirm"
            >
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment receipt dialog */}
      <Dialog open={!!paymentReceipt} onOpenChange={(o) => !o && setPaymentReceipt(null)}>
        <DialogContent className="max-w-sm" data-testid="quick-payment-receipt-dialog">
          <DialogHeader>
            <DialogTitle>📄 Comprobante de pago</DialogTitle>
            <DialogDescription>Guarda o comparte este comprobante con el cliente.</DialogDescription>
          </DialogHeader>
          {paymentReceipt && (
            <div id="quick-receipt-printable" className="font-mono text-xs bg-white p-3 border rounded">
              <div className="text-center font-bold mb-2">— COMPROBANTE DE PAGO —</div>
              <div>Cliente: <b>{paymentReceipt.customer_name}</b></div>
              <div>Fecha: {paymentReceipt.paid_at || new Date().toLocaleString("es-CR")}</div>
              <div>Método: {paymentReceipt.transaction_type || debtForm.transaction_type}</div>
              {paymentReceipt.payment_reference && <div>Ref.: {paymentReceipt.payment_reference}</div>}
              <div className="border-t my-2" />
              <div>Saldo anterior: ₡{Number(paymentReceipt.previous_balance_colones || 0).toLocaleString("es-CR")}</div>
              <div>Pago recibido: ₡{Number(paymentReceipt.amount_paid_colones || 0).toLocaleString("es-CR")}</div>
              {paymentReceipt.amount_paid_dollars > 0 && <div>Pago recibido: ${Number(paymentReceipt.amount_paid_dollars).toLocaleString("es-CR")}</div>}
              <div className="font-bold mt-1">Saldo actual: ₡{Number(paymentReceipt.remaining_colones || 0).toLocaleString("es-CR")}</div>
              {paymentReceipt.note && <div className="mt-1 text-slate-600">Nota: {paymentReceipt.note}</div>}
              <div className="text-center mt-2 text-[10px] text-slate-500">Generado por PaliStore</div>
            </div>
          )}
          <DialogFooter className="flex gap-1 flex-wrap">
            <Button variant="outline" onClick={() => setPaymentReceipt(null)} data-testid="quick-receipt-close">Cerrar</Button>
            <Button
              variant="outline"
              data-testid="quick-receipt-print"
              onClick={() => {
                const node = document.getElementById("quick-receipt-printable");
                if (!node) return;
                const w = window.open("", "_blank", "width=400,height=600");
                w.document.write(`<html><body>${node.outerHTML}<script>window.print();</script></body></html>`);
                w.document.close();
              }}
            >
              Imprimir
            </Button>
            {paymentReceipt?.customer_phone && (
              <Button
                data-testid="quick-receipt-whatsapp"
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={() => {
                  const r = paymentReceipt;
                  const lines = [
                    `*Comprobante de pago*`,
                    `Cliente: ${r.customer_name}`,
                    `Fecha: ${r.paid_at || new Date().toLocaleString("es-CR")}`,
                    `Método: ${r.transaction_type || debtForm.transaction_type}`,
                    r.payment_reference ? `Ref: ${r.payment_reference}` : null,
                    `Saldo anterior: ₡${Number(r.previous_balance_colones || 0).toLocaleString("es-CR")}`,
                    `Pago recibido: ₡${Number(r.amount_paid_colones || 0).toLocaleString("es-CR")}`,
                    `*Saldo actual: ₡${Number(r.remaining_colones || 0).toLocaleString("es-CR")}*`,
                    `— PaliStore`,
                  ].filter(Boolean).join("\n");
                  const phone = String(r.customer_phone).replace(/\D/g, "");
                  const p = phone.startsWith("506") ? phone : "506" + phone;
                  window.open(`https://wa.me/${p}?text=${encodeURIComponent(lines)}`, "_blank");
                }}
              >
                WhatsApp
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
